import { Button, Checkbox, Collapse, Slider } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { CaretRightOutlined } from '@ant-design/icons';
import { PropertySectors } from '../../../components/shared/PropertiesMap/propertySectors';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import BuildingDirectoryInfo from '../models/buildingDirectoryInfo';
import BuildingDirectoryCard from './BuildingDirectoryCard';

const tenantOptions = [
    { label: 'Technology', value: 'Technology' },
    { label: 'Banks', value: 'Banks' },
    { label: 'Insurance', value: 'Insurance' },
    { label: 'Consultancies', value: 'Consultancies' },
    { label: 'Trading', value: 'Trading' },
];

const sectorOptions = [
    { label: PropertySectors.office, value: PropertySectors.office },
    { label: PropertySectors.retail, value: PropertySectors.retail },
    { label: PropertySectors.hotel, value: PropertySectors.hotel },
    { label: PropertySectors.school, value: PropertySectors.school },
];

interface Props {
    minPrice: number;
    maxPrice: number;
    selectedTenants: string[];
    selectedSectors: string[];
    selectedPriceRange: [number, number];
    onTenantsChanged: (values: string[]) => void;
    onSectorsChanged: (values: string[]) => void;
    onPriceChanged: (values: [number, number]) => void;
    results: BuildingDirectoryInfo[];
    onBuildingClicked: (buildingDirectory: BuildingDirectoryInfo) => void;
}

const FilterMenuPanel: FunctionComponent<Props> = ({
    minPrice,
    maxPrice,
    selectedTenants,
    selectedSectors,
    selectedPriceRange,
    onTenantsChanged,
    onSectorsChanged,
    onPriceChanged,
    results,
    onBuildingClicked
}) => {
    const [priceMin, setPriceMin] = useState<number>(selectedPriceRange[0]),
        [priceMax, setPriceMax] = useState<number>(selectedPriceRange[1]),
        [draftTenants, setDraftTenants] = useState<string[]>(selectedTenants),
        [draftSectors, setDraftSectors] = useState<string[]>(selectedSectors);

    const onPriceChange = value => {
        setPriceMin(value[0]);
        setPriceMax(value[1]);
    };

    const onTenantChange = (values: CheckboxValueType[]) => {
        setDraftTenants(values.map(v => String(v)));
    };

    const onSectorChange = (values: CheckboxValueType[]) => {
        setDraftSectors(values.map(v => String(v)));
    };

    const handleApplyFilters = () => {
        onTenantsChanged(draftTenants);
        onSectorsChanged(draftSectors);
        onPriceChanged([priceMin, priceMax]);
    };

    const handleResetFilters = () => {
        setDraftTenants([]);
        setDraftSectors([]);
        setPriceMin(minPrice);
        setPriceMax(maxPrice);
        onTenantsChanged([]);
        onSectorsChanged([]);
        onPriceChanged([minPrice, maxPrice]);
    };

    useEffect(() => {
        setPriceMin(selectedPriceRange[0]);
        setPriceMax(selectedPriceRange[1]);
        setDraftTenants(selectedTenants);
        setDraftSectors(selectedSectors);
    }, [selectedPriceRange, selectedTenants, selectedSectors]);

    return (
        <>
            <div className='search-name'>Filter our Building Directory</div>
            <div className='search-address'>Use the filters below to refine building results.</div>
            <Collapse
                bordered={false}
                defaultActiveKey={['1', '2']}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                className="filter-collapse"
            >
                <Collapse.Panel header="Tenants by Industry" key="1">
                    <Checkbox.Group className='checkbox-group' options={tenantOptions} value={draftTenants} onChange={onTenantChange} />

                    <div className='price-title'>Price range</div>
                    <Slider className='price-slider' onAfterChange={onPriceChange} range value={[priceMin, priceMax]} min={minPrice} max={maxPrice} />
                    <div className='price-value'>
                        <span>{priceMin} psm</span>
                        <span className='max-price'>{priceMax} psm</span>
                    </div>
                </Collapse.Panel>
                <Collapse.Panel header="Properties by sector" key="2">
                    <Checkbox.Group className='checkbox-group sector' options={sectorOptions} value={draftSectors} onChange={onSectorChange} />
                </Collapse.Panel>
                <Collapse.Panel header="Commute scores" key="3">
                    <p>TBD</p>
                </Collapse.Panel>
            </Collapse>
            <div className='d-flex align-items-center' style={{ padding: '12px 16px 16px 32px', gap: 10 }}>
                <Button type='primary' onClick={handleApplyFilters}>Apply Filters</Button>
                <Button onClick={handleResetFilters}>Reset</Button>
            </div>
            <div className='search-result'>
                {results.map(building => (
                    <BuildingDirectoryCard
                        key={building.id}
                        buildingDirectory={building}
                        onClicked={onBuildingClicked}
                    />
                ))}
            </div>
        </>
    );
};

export default FilterMenuPanel;
